/**
 * Realization layer: wave-function collapse over the path domain.
 *
 * The slot universe is the skeleton ring. Candidate states are (pieceVariant,
 * startIndex) windows whose move signature matches the ring at that position;
 * constraints are coverage (each ring cell exactly once), elevation (flat
 * windows only span constant base height; reserved climb windows are
 * pre-restricted), and 2D occupancy (multi-cell pieces' extra footprint must
 * not collide with the rest of the track). Min-entropy collapse with
 * style-weighted draws; the single-cell fallback (straight / corner) always
 * exists, so generation cannot contradict — occupancy vetoes just remove
 * candidates.
 */
import type { Dir } from "../shared/types.ts";
import type { Rng } from "./rng.ts";
import type { Skeleton } from "./topology.ts";
import type { ElevationPlan, Reservation } from "./elevation.ts";
import type { StyleField } from "./style.ts";
import type { PieceDef, PieceRegistry } from "./registry.ts";
import { applyXform, windowXform } from "./geom.ts";

export interface RealizedWindow {
  start: number;
  len: number;
  def: PieceDef;
}

export interface RealizeOptions {
  /**
   * Open path (endless mode): candidate windows must not wrap past the last
   * cell, and the width-continuity check skips the nonexistent seam. Closed
   * rings (the default) are unaffected.
   */
  open?: boolean;
  /**
   * Extra veto for footprint-extra cells — endless mode consults the world
   * occupancy here (other segments, island masses) since the skeleton only
   * covers this segment. Returning false rejects the candidate.
   */
  extraFree?: (x: number, z: number, baseY: number) => boolean;
  /**
   * Exclude free-collapse candidates (endless physics: pieces whose
   * traversal demands momentum, or lateral pieces over set-piece cells
   * whose trajectory math assumes a straight channel). `start` is the
   * candidate window's start index. Reservations are unaffected.
   */
  exclude?: (def: PieceDef, start: number) => boolean;
}

interface Candidate extends RealizedWindow {
  weight: number;
}

const key = (x: number, z: number) => `${x},${z}`;

export function realize(
  rng: Rng,
  skeleton: Skeleton,
  elevation: ElevationPlan,
  style: StyleField,
  registry: PieceRegistry,
  features: readonly Reservation[] = [],
  opts: RealizeOptions = {},
): RealizedWindow[] {
  const n = skeleton.cells.length;
  const moves = skeleton.cells.map((c) => c.move).join("");
  const covered = new Array<boolean>(n).fill(false);
  const committed: RealizedWindow[] = [];

  // Occupancy: every skeleton cell is track-reserved from the start; extra
  // footprint cells of committed multi-cell pieces get added as we collapse.
  // Values carry the deck base height at that cell — plate-wing support
  // must be SAME-HEIGHT track (a channel a level below a platform wing
  // doesn't visually carry it).
  const occupied = new Map<string, number>();
  skeleton.cells.forEach((c, i) =>
    occupied.set(key(c.x, c.z), elevation.baseY[i])
  );

  const reservedCells = new Set<number>();

  /** Footprint cells of a def at `start` that fall outside its window. */
  const extraCellsOf = (def: PieceDef, start: number): [number, number][] => {
    const cell = skeleton.cells[start % n];
    const xf = windowXform(cell.x, cell.z, cell.dirIn, 0);
    const windowSet = new Set<string>();
    for (let j = 0; j < def.moves.length; j++) {
      const c = skeleton.cells[(start + j) % n];
      windowSet.add(key(c.x, c.z));
    }
    const extras: [number, number][] = [];
    for (const [fx, fz] of def.footprint) {
      const w = applyXform(xf, [fx, 0, fz]);
      const k = key(Math.round(w[0]), Math.round(w[2]));
      if (!windowSet.has(k)) extras.push([Math.round(w[0]), Math.round(w[2])]);
    }
    return extras;
  };

  // 1. Commit reservations (pre-collapsed slots) from the elevation planner
  // (climbs/descents) and the feature planner (wide runs). Their extra
  // footprint cells (wide pieces bulge into flanking cells) go straight into
  // occupancy so free collapse routes multi-cell pieces around them.
  for (const res of [...elevation.reservations, ...features]) {
    const candidates = registry.variants.filter((v) =>
      res.pieceIds.includes(v.id) && Boolean(v.reversed) === res.reversed &&
      v.moves.length === res.len
    );
    if (candidates.length === 0) {
      throw new Error(
        `no variant for reservation ${res.pieceIds} rev=${res.reversed}`,
      );
    }
    const def = candidates.length === 1
      ? candidates[0]
      : rng.child(`reserve/${res.start}`).weighted(
        candidates.map((c) =>
          [c, c.baseWeight * style.weightFor(c, res.start)] as const
        ),
      );
    committed.push({ start: res.start, len: res.len, def });
    for (let j = 0; j < res.len; j++) {
      covered[(res.start + j) % n] = true;
      reservedCells.add((res.start + j) % n);
    }
    for (const [x, z] of extraCellsOf(def, res.start)) {
      occupied.set(key(x, z), elevation.baseY[res.start % n]);
    }
  }

  // 2. Enumerate flat candidates: windows matching the ring's move string at
  // uncovered positions with constant base height. (Windows never wrap the
  // seam in candidate enumeration when they'd cross a covered cell — wrap
  // across index 0 is fine as long as cells are uncovered; handled modulo.)
  const candidateOk = (def: PieceDef, start: number): boolean => {
    const len = def.moves.length;
    if (opts.open && start + len > n) return false;
    for (let j = 0; j < len; j++) {
      const idx = (start + j) % n;
      if (covered[idx] || reservedCells.has(idx)) return false;
      if (moves[idx] !== def.moves[j]) return false;
      // Flat windows only: base height constant across the window.
      if (elevation.baseY[idx] !== elevation.baseY[start]) return false;
    }
    if (def.deckDelta !== 0) return false;
    // Occupancy: extra footprint cells must be free — locally, and (open
    // mode) against the world beyond this segment.
    for (const [x, z] of extraCellsOf(def, start)) {
      if (occupied.has(key(x, z))) return false;
      if (opts.extraFree && !opts.extraFree(x, z, elevation.baseY[start])) {
        return false;
      }
    }
    // Plate tiles: the connected deck must lie along AT LEAST ONE platform
    // wing (user rule), at the SAME base height, or the slab reads as a
    // bare platform jutting into grass — corners stay the default.
    // (Port-edge neighbors don't count; they touch every piece by
    // construction.)
    if (def.plateSides) {
      const cell = skeleton.cells[start % n];
      const xf = windowXform(cell.x, cell.z, cell.dirIn, 0);
      const anyWing = def.plateSides.some(([px, pz]) => {
        const w = applyXform(xf, [px, 0, pz]);
        return occupied.get(key(Math.round(w[0]), Math.round(w[2]))) ===
          elevation.baseY[start % n];
      });
      if (!anyWing) return false;
    }
    return true;
  };

  const allCandidates: Candidate[] = [];
  for (let start = 0; start < n; start++) {
    for (const def of registry.variants) {
      if (def.reservedOnly) continue;
      if (opts.exclude?.(def, start)) continue;
      if (def.moves.length > n) continue;
      if (candidateOk(def, start)) {
        const weight = def.baseWeight * style.weightFor(def, start);
        if (weight > 0) {
          allCandidates.push({ start, len: def.moves.length, def, weight });
        }
      }
    }
  }

  // 3. Min-entropy collapse: repeatedly pick the uncovered cell with the
  // fewest valid candidates covering it, then draw one by weight.
  const collapseRng = rng.child("collapse");
  let guard = 0;
  while (covered.some((c) => !c)) {
    if (guard++ > n * 4) throw new Error("wfc did not converge");

    // Valid candidates per uncovered index.
    const covering = new Map<number, Candidate[]>();
    for (const cand of allCandidates) {
      if (!candidateOk(cand.def, cand.start)) continue;
      for (let j = 0; j < cand.len; j++) {
        const idx = (cand.start + j) % n;
        if (covered[idx]) continue;
        const list = covering.get(idx) ?? [];
        list.push(cand);
        covering.set(idx, list);
      }
    }

    let target = -1;
    let best = Infinity;
    for (let idx = 0; idx < n; idx++) {
      if (covered[idx]) continue;
      const count = covering.get(idx)?.length ?? 0;
      if (count === 0) {
        throw new Error(`cell ${idx} has no candidates (fallback missing?)`);
      }
      if (count < best) {
        best = count;
        target = idx;
      }
    }

    const options = covering.get(target)!;
    const chosen = collapseRng.weighted(
      options.map((c) => [c, c.weight] as const),
    );
    committed.push({ start: chosen.start, len: chosen.len, def: chosen.def });
    for (let j = 0; j < chosen.len; j++) covered[(chosen.start + j) % n] = true;
    for (const [x, z] of extraCellsOf(chosen.def, chosen.start)) {
      occupied.set(key(x, z), elevation.baseY[chosen.start % n]);
    }
  }

  committed.sort((a, b) => a.start % n - b.start % n);

  // Width continuity: at every boundary between adjacent windows, the exit
  // channel width must equal the next window's entry width. The feature
  // planner guarantees this by construction (wide runs are funnel-bracketed
  // reservations); free candidates are all narrow–narrow. Hard invariant.
  const inW = new Map<number, string>();
  const outW = new Map<number, string>();
  for (const w of committed) {
    // Open paths have no seam: the exit boundary of the last window (index
    // n) matches no entry and is rightly skipped.
    inW.set(w.start % n, w.def.widthIn ?? "narrow");
    outW.set(
      opts.open ? w.start + w.len : (w.start + w.len) % n,
      w.def.widthOut ?? "narrow",
    );
  }
  for (const [b, wIn] of inW) {
    const wOut = outW.get(b);
    if (wOut !== undefined && wOut !== wIn) {
      throw new Error(`channel width mismatch at ring boundary ${b}`);
    }
  }

  return committed;
}

/** World heading into a window's entry cell — convenience for assembly. */
export function windowHeading(skeleton: Skeleton, start: number): Dir {
  return skeleton.cells[start].dirIn;
}
